import { Component, EventEmitter, input, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import {
    BuildingView,
    QuickSearchBuildingParameters,
    SearchAvailableCompanyBuildingsParameters,
    SearchAvailableContactBuildingsParameters,
    SearchAvailableOpportunityBuildingsParameters
} from '@api-proxies';
import { BuildingService } from '@http/clients/building.service';
import { CompanyService } from '@http/clients/company.service';
import { ContactService } from '@http/clients/contact.service';
import { OpportunityBuildingService } from '@http/clients/opportunity-building.service';
import { ITypeAheadDropdownEntityParameters } from '@interfaces/type-ahead-dropdown-entity-parameters.interface';
import { EntityTypes } from '@models/enums/EntityTypes';
import { GenericTypeAheadDropdownComponent, HighlightTextPipe, ITypeAheadDropdownAction, ModalService } from 'ngx-lib';
import { ModalAddBuildingComponent } from '../modals/modal-add-building/modal-add-building.component';

@Component({
    selector: 'app-type-ahead-buildings',
    templateUrl: './type-ahead-buildings.component.html',
    styleUrls: ['./type-ahead-buildings.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [GenericTypeAheadDropdownComponent, HighlightTextPipe]
})
export class TypeAheadBuildingsComponent implements OnInit {
    @Input()
    selectedBuilding?: BuildingView;

    @Input()
    isReadOnly?: boolean;

    @Input()
    displaySelected?: boolean;

    @Input()
    searchParameters!: ITypeAheadDropdownEntityParameters;

    @Input()
    isActive?: boolean;

    @Input()
    contactId?: number;

    @Output()
    selectedBuildingChange = new EventEmitter<BuildingView>();

    @Input()
    error?: boolean;

    placeholder = input<string | undefined>('Add Building');

    path = input<string | undefined>();

    searchText: string;

    source?: BuildingView[];

    addBuildingAction!: ITypeAheadDropdownAction;

    constructor(
        private readonly buildingService: BuildingService,
        private readonly contactService: ContactService,
        private readonly companyService: CompanyService,
        private readonly opportunityBuildingService: OpportunityBuildingService,
        private readonly modalService: ModalService
    ) {
        this.searchText = '';
        this.isReadOnly = false;
        this.displaySelected = false;
        this.error = false;
    }

    async ngOnInit(): Promise<void> {
        this.addBuildingAction = {
            includeAction: true,
            name: '+ Add new building',
            action: () => this.openModal()
        };
    }

    async openModal(): Promise<void> {
        this.selectedBuilding = await this.modalService.open(ModalAddBuildingComponent);
        if (this.selectedBuilding) {
            this.selectedBuildingChange.emit(this.selectedBuilding);
        }
    }

    async searchByEntity(entityId?: EntityTypes): Promise<BuildingView[]> {
        const searchConfig: unknown = {
            filterText: this.searchText,
            ...this.searchParameters?.entity,
            ...(this.contactId && { contactId: this.contactId }),
            isActive: this.isActive
        };

        this.source = [];

        switch (entityId) {
            case EntityTypes.Company:
                return await this.companyService.searchAvailableBuildings(
                    searchConfig as SearchAvailableCompanyBuildingsParameters
                );

            case EntityTypes.Contact:
                return await this.contactService.searchAvailableBuildings(
                    searchConfig as SearchAvailableContactBuildingsParameters
                );

            case EntityTypes.Opportunity: {
                return await this.opportunityBuildingService.searchAvailable(
                    searchConfig as SearchAvailableOpportunityBuildingsParameters
                );
            }

            default:
                return await this.buildingService.search(searchConfig as QuickSearchBuildingParameters);
        }
    }

    async onBuildingTextChanged(): Promise<void> {
        if (!this.searchText) return;

        this.source = await this.searchByEntity(this.searchParameters?.entityKeyName);
    }

    onBuildingChange(itemSelected: BuildingView): void {
        this.selectedBuilding = itemSelected;
        this.selectedBuildingChange.emit(itemSelected);
    }
}
